import React from 'react';
import {
  Box,
  Card,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const OrderManagementPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const cardCount = isMobile ? 3 : isTablet ? 4 : 8;

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: '#f8fafc',
      }}>
        {/* Sticky Header — edge-to-edge with ::after rounded transition */}
        <Box sx={{
          mx: { xs: -2, md: -3 },
          mt: { xs: -2, md: -3 },
          mb: '20px',
          background: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          px: { xs: 2, md: 3 },
          pt: { xs: 1.5, md: 2 },
          pb: 1.5,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -20,
            left: 0,
            right: 0,
            height: 20,
            background: '#f8fafc',
            borderRadius: '20px 20px 0 0',
          },
        }}>
          {/* Title row */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            mb: 1.5,
          }}>
            <Skeleton
              height={isMobile ? 22 : 28}
              width={isMobile ? 80 : 110}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Skeleton height={32} width={68} style={{ borderRadius: '10px' }} />
              <Skeleton height={32} width={32} style={{ borderRadius: '10px' }} />
              <Skeleton height={28} width={32} style={{ borderRadius: '14px' }} />
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ mb: 1.5 }}>
            <Skeleton
              height={36}
              width={isMobile ? '100%' : 400}
              style={{ borderRadius: '12px' }}
            />
          </Box>

          {/* Tabs */}
          <Box sx={{
            background: '#f8fafc',
            borderRadius: '20px',
            padding: '6px',
            display: 'flex',
            gap: '4px',
          }}>
            {[...Array(3)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  bgcolor: index === 0 ? 'white' : 'transparent',
                  borderRadius: '12px',
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Skeleton
                  circle
                  width={10}
                  height={10}
                />
                <Skeleton
                  height={14}
                  width={isMobile ? 45 : 65}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Cards Grid */}
        <Box sx={{
          flex: 1,
          px: { xs: 2, md: 4 },
          pb: 4,
        }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
          }}>
            {[...Array(cardCount)].map((_, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Banner */}
                <Box sx={{
                  background: '#f9fafb',
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Skeleton
                    height={22}
                    width={isMobile ? 130 : 140}
                    style={{ borderRadius: '6px' }}
                  />
                </Box>

                {/* Info strip */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  background: '#f9fafb',
                }}>
                  <Skeleton height={24} width={55} style={{ borderRadius: '12px' }} />
                  <Skeleton height={24} width={isMobile ? 70 : 80} style={{ borderRadius: '12px' }} />
                  <Skeleton height={24} width={50} style={{ borderRadius: '12px' }} />
                </Box>

                {/* Payment status */}
                <Box sx={{
                  px: 2,
                  py: 0.75,
                  background: '#f9fafb',
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <Skeleton height={24} width={100} style={{ borderRadius: '12px' }} />
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {/* Customer & total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Skeleton circle width={28} height={28} />
                      <Skeleton height={14} width={isMobile ? 70 : 90} />
                    </Box>
                    <Skeleton height={16} width={60} />
                  </Box>

                  {/* Items */}
                  <Box sx={{
                    background: '#f9fafb',
                    borderRadius: '10px',
                    p: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                  }}>
                    {[...Array(2)].map((_, itemIdx) => (
                      <Box key={itemIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Skeleton height={14} width={22} style={{ borderRadius: '4px' }} />
                        <Skeleton height={13} width={isMobile ? 90 : 110} />
                      </Box>
                    ))}
                  </Box>

                  {/* Order notes placeholder */}
                  {index % 3 === 0 && (
                    <Box sx={{
                      bgcolor: '#f9fafb',
                      border: '1px solid rgba(0,0,0,0.05)',
                      borderRadius: '8px',
                      px: 1.25,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}>
                      <Skeleton circle width={12} height={12} />
                      <Skeleton height={11} width={isMobile ? 100 : 140} />
                    </Box>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  gap: 1,
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                }}>
                  <Skeleton height={34} style={{ flex: 1, borderRadius: '10px' }} />
                  <Skeleton height={34} width={70} style={{ borderRadius: '10px' }} />
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </SkeletonTheme>
  );
};

export default OrderManagementPageSkeleton;
